/**
 * CG の部屋の広さを測る。
 *
 * 間取り図のように「リビング 24.4帖」と出すために、部屋ごとの床面積が要る。
 * 設計データには部屋の区画が書かれていないので、壁で仕切られた床のつながりから測る。
 *
 * 1. 視点と同じ階の床を 10cm のマスに塗る
 * 2. その階の壁をマスに塗って仕切りにする（戸や引き違いの開口部も仕切りとして数える。
 *    開けたままにすると隣の部屋までつながって、ひと続きの広さになってしまう）
 * 3. 視点の位置から届いたマスの数を面積にする
 */
const CELL = 0.1;
/** 1帖の広さ(㎡)。不動産の広告で使う中京間 1.62㎡ に合わせる */
const TATAMI_M2 = 1.62;
/** 視点の目線から、その階の床までの高さ(m) */
const EYE_HEIGHT = 1.45;
/** 床とみなす高さの差(m)。床板の厚みぶんの誤差を吸収する */
const FLOOR_TOLERANCE = 0.6;
/** 仕切りかどうかを見る高さ（床から m）。腰より上にある壁だけを仕切りとして数える */
const WALL_AT = 1.2;

/**
 * ひとつの部屋に視点が複数あることがある（LDK にリビング・ダイニング・小上がりなど）。
 * その場合は同じ広さを何度も出しても仕方がないので、同じ区画の視点には同じ room を付け、
 * 呼ぶ側が代表の1つにだけ広さを出せるようにしている。
 *
 * @param plan  buildHouse の設計データ
 * @param spots [{ name, position: [x, y, z] }] 部屋ごとの視点
 * @returns Map<部屋名, { m2, jo, room }> room は同じ区画かどうかの目印
 */
export function measureRooms(plan, spots) {
  const out = new Map();
  for (const spot of spots) {
    const foot = spot.position[1] - EYE_HEIGHT;
    const floors = plan.rooms.filter(
      (room) => room.floor !== undefined && Math.abs((room.y ?? 0) - foot) <= FLOOR_TOLERANCE,
    );
    if (floors.length === 0) continue;
    const walls = plan.walls.filter((wall) => {
      const bottom = wall.y ?? 0;
      return bottom <= foot + WALL_AT && bottom + wall.h >= foot + WALL_AT;
    });
    const { m2, room } = floodArea(floors, walls, spot.position[0], spot.position[2]);
    // 目印には階も入れる。1階と2階で同じ位置に部屋があると、同じ部屋とみなされてしまう
    if (m2 > 0) {
      out.set(spot.name, { m2: round(m2, 1), jo: round(m2 / TATAMI_M2, 1), room: `${round(foot, 1)}@${room}` });
    }
  }
  return out;
}

/**
 * 開始地点から、壁に区切られた床をたどった面積(㎡)と、その区画の目印。
 * 目印には「たどり着いたマスのうち一番はじのもの」を使う。同じ部屋なら必ず同じ値になる。
 */
function floodArea(floors, walls, startX, startZ) {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const room of floors) {
    x0 = Math.min(x0, room.x[0]);
    x1 = Math.max(x1, room.x[1]);
    z0 = Math.min(z0, room.z[0]);
    z1 = Math.max(z1, room.z[1]);
  }
  const nx = Math.ceil((x1 - x0) / CELL);
  const nz = Math.ceil((z1 - z0) / CELL);
  if (nx <= 0 || nz <= 0) return EMPTY;

  // 通れるマス（床があって壁でない）を作る
  const open = new Uint8Array(nx * nz);
  for (let iz = 0; iz < nz; iz += 1) {
    for (let ix = 0; ix < nx; ix += 1) {
      const x = x0 + (ix + 0.5) * CELL;
      const z = z0 + (iz + 0.5) * CELL;
      if (!floors.some((r) => x >= r.x[0] && x <= r.x[1] && z >= r.z[0] && z <= r.z[1])) continue;
      if (walls.some((w) => nearSegment(x, z, w))) continue;
      open[iz * nx + ix] = 1;
    }
  }

  const sx = Math.floor((startX - x0) / CELL);
  const sz = Math.floor((startZ - z0) / CELL);
  const from = nearestOpen(open, nx, nz, sx, sz);
  if (from === null) return EMPTY;

  const seen = new Uint8Array(nx * nz);
  const stack = [from];
  seen[from] = 1;
  let count = 0;
  // 同じ部屋かどうかの目印。たどり着いたマスのうち一番はじのものを使う
  let edge = from;
  while (stack.length > 0) {
    const at = stack.pop();
    count += 1;
    if (at < edge) edge = at;
    const ix = at % nx;
    const iz = (at - ix) / nx;
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const jx = ix + dx;
      const jz = iz + dz;
      if (jx < 0 || jx >= nx || jz < 0 || jz >= nz) continue;
      const next = jz * nx + jx;
      if (seen[next] === 1 || open[next] === 0) continue;
      seen[next] = 1;
      stack.push(next);
    }
  }
  const ex = x0 + (edge % nx) * CELL;
  const ez = z0 + Math.floor(edge / nx) * CELL;
  return { m2: count * CELL * CELL, room: `${round(ex, 1)},${round(ez, 1)}` };
}

/** 床も壁も無くて測れなかったとき */
const EMPTY = Object.freeze({ m2: 0, room: null });

/**
 * 開始地点が家具や壁の中に入っていることがあるので、近くの通れるマスを探す。
 * 見つからなければ null（その視点の広さは出さない）。
 */
function nearestOpen(open, nx, nz, sx, sz) {
  for (let r = 0; r <= 12; r += 1) {
    for (let dz = -r; dz <= r; dz += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
        const ix = sx + dx;
        const iz = sz + dz;
        if (ix < 0 || ix >= nx || iz < 0 || iz >= nz) continue;
        const at = iz * nx + ix;
        if (open[at] === 1) return at;
      }
    }
  }
  return null;
}

/** その点が壁の厚みの中にあるか */
function nearSegment(x, z, wall) {
  const [ax, az] = wall.from;
  const [bx, bz] = wall.to;
  const dx = bx - ax;
  const dz = bz - az;
  const len2 = dx * dx + dz * dz;
  const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - ax) * dx + (z - az) * dz) / len2));
  const px = ax + dx * t;
  const pz = az + dz * t;
  const half = (wall.t ?? 0.12) / 2 + CELL / 2;
  return (x - px) ** 2 + (z - pz) ** 2 <= half * half;
}

const round = (v, digits) => Math.round(v * 10 ** digits) / 10 ** digits;
